import type { AccountInfo } from '@azure/msal-browser';
import { EventType, PublicClientApplication } from '@azure/msal-browser';

import { buildMsalConfig, buildMsalScopes } from '../config/auth-config';
import {
  clearLoginAttempts,
  hasExceededLoginAttempts,
  MAX_LOGIN_ATTEMPTS,
  recordLoginAttempt,
} from '../config/login-loop-guard';
import { TenantConfig } from '../config/tenant-config';
import { postLoginLog } from '../hooks/useLoginLog';
import { logger } from '../services/logger';

class MsalInstanceManager {
  private static instance: MsalInstanceManager | null = null;
  private msalInstance: PublicClientApplication | null = null;
  private apiScopes: string[] = [];
  private presenceScope = '';
  private isLoggingOut = false;

  // WI-F3: no PublicClientApplication is constructed at import / getInstance()
  // time anymore. It is built from the runtime TenantConfig in initMsal(),
  // AFTER `GET /api/v1/config` has resolved (see main.tsx boot sequence).
  private constructor() {}

  /**
   * Configure the manager from the runtime {@link TenantConfig} and construct
   * the underlying `PublicClientApplication`. Idempotent-ish: called once at
   * boot before any consumer reads the instance or scopes.
   */
  private configure(tenant: TenantConfig): void {
    this.msalInstance = new PublicClientApplication(buildMsalConfig(tenant));
    const scopes = buildMsalScopes(tenant);
    this.apiScopes = scopes.api;
    this.presenceScope = scopes.presence;
  }

  private requireInstance(): PublicClientApplication {
    if (this.msalInstance === null) {
      throw new Error(
        'MsalInstanceManager used before initMsal(tenantConfig). Runtime ' +
          'tenant config must be fetched and initMsal() awaited during boot ' +
          'before any MSAL access.'
      );
    }
    return this.msalInstance;
  }

  /**
   * WI-F3: receives the runtime {@link TenantConfig}, builds the PCA from it,
   * then runs the (unchanged) init + redirect-return + login-log flow.
   */
  async initMsal(tenant: TenantConfig) {
    try {
      this.configure(tenant);
      const msalInstance = this.requireInstance();

      await msalInstance.initialize();

      const result = await msalInstance.handleRedirectPromise();

      // event arg type should be EventType of msal-browser.
      // But it doesn't work for event.payload.account for some reason.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      msalInstance.addEventCallback((event: any) => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          event.eventType === EventType.SSO_SILENT_SUCCESS
        ) {
          const account = event.payload?.account;
          if (!account) return;

          // The e-sign ceremony (WI-F7) re-authenticates through
          // `acquireTokenPopup({ prompt: 'login' })`, which raises
          // ACQUIRE_TOKEN_SUCCESS like any other token acquisition. `loginHint`
          // only PREFILLS that screen, so a user can complete it as a different
          // identity — and switching the active account here would silently
          // re-point the whole session (and every subsequent API token) at that
          // other account. An established active account is therefore never
          // replaced from an event; only a genuinely new sign-in sets one.
          const activeAccount = msalInstance.getActiveAccount();
          if (
            activeAccount &&
            activeAccount.homeAccountId !== account.homeAccountId
          ) {
            logger.error(
              new Error(
                'Ignoring an MSAL success event for a different account than ' +
                  'the active session account.'
              ),
              { origin: 'MsalInstanceManager', eventType: event.eventType }
            );
            return;
          }

          msalInstance.setActiveAccount(account);
        }
      });

      const activeAccount = msalInstance.getActiveAccount();

      if (activeAccount !== null) {
        // Authenticated from a cached session — clear the loop guard so a
        // future re-auth starts with a fresh budget.
        clearLoginAttempts();
      } else {
        if (result === null) {
          // No cached account and no redirect response to process — we would
          // normally kick off interactive login. Bound this: if we have
          // already burned the automatic-redirect budget for this session
          // (e.g. the token exchange keeps failing on return), STOP and throw
          // so boot renders the error screen instead of looping /authorize
          // forever (ADO #38557).
          if (hasExceededLoginAttempts()) {
            clearLoginAttempts();
            throw new Error(
              `MSAL login loop detected: exceeded ${MAX_LOGIN_ATTEMPTS} ` +
                'automatic login redirects without acquiring an account. ' +
                'Aborting redirect to avoid an infinite login loop.'
            );
          }

          recordLoginAttempt();
          await msalInstance.loginRedirect({
            scopes: this.apiScopes,
            prompt: 'select_account',
          });
        }

        if (result) {
          // Redirect returned with a usable response — login succeeded, reset
          // the loop guard before continuing.
          clearLoginAttempts();

          const accessToken = await this.getTokenSilently(false);

          postLoginLog({
            isSuccessful: true,
            accessToken: accessToken ?? '',
            requestId: result.requestId,
            userDetails: {
              userName: result.account?.name,
              oid: result.account?.idTokenClaims?.oid,
              tenantId: result.account?.tenantId,
            },
          });
        }
      }

      return msalInstance;
    } catch (err) {
      logger.error(err as Error, {
        origin: 'MsalInstanceManager',
      });
      // Re-throw so a failed initialize()/handleRedirectPromise() surfaces to
      // the boot sequence (main.tsx) and renders the msal-error screen instead
      // of leaving a half-built PCA and letting boot reach `ready`.
      throw err;
    }
  }

  public static getInstance(): MsalInstanceManager {
    if (this.instance === null) {
      this.instance = new MsalInstanceManager();
    }

    return this.instance;
  }

  /** Test-only: drop the singleton (and its PublicClientApplication) between cases. */
  public static reset(): void {
    this.instance = null;
  }

  public getMsalInstance(): PublicClientApplication {
    return this.requireInstance();
  }

  /**
   * The account every token request is made for.
   *
   * Prefers the ACTIVE account over `getAllAccounts()[0]`: the e-sign popup can add a second account
   * to the cache, and `getAllAccounts()` has no defined ordering — picking `[0]` could silently
   * acquire API tokens for the wrong identity.
   */
  public getSessionAccount(): AccountInfo | undefined {
    const msalInstance = this.requireInstance();
    return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  }

  /** Injected API scopes (from the runtime TenantConfig). */
  public getApiScopes(): string[] {
    return this.apiScopes;
  }

  /** Injected (dormant) Graph presence scope. */
  public getPresenceScope(): string {
    return this.presenceScope;
  }

  public setLoggingOut(value: boolean) {
    this.isLoggingOut = value;
  }

  public getLoggingOut(): boolean {
    return this.isLoggingOut;
  }

  public async getTokenSilently(isGraphRequest?: boolean) {
    // Don't attempt token acquisition if logout is in progress
    if (this.isLoggingOut) {
      return null;
    }

    const msalInstance = this.requireInstance();
    const account = this.getSessionAccount();

    const tokenRequest = {
      scopes: isGraphRequest ? [this.presenceScope] : this.apiScopes,
      // Unchanged from the previous `getAllAccounts()[0]`: with nothing cached this is undefined and
      // MSAL rejects with `no_account_error`, which the catch below turns into a logout redirect.
      account: account as AccountInfo,
    };

    try {
      const { accessToken } =
        await msalInstance.acquireTokenSilent(tokenRequest);

      return accessToken;
    } catch (error: unknown) {
      // Don't trigger logout if already logging out (prevents race conditions)
      if (this.isLoggingOut) {
        return null;
      }

      if (
        error instanceof Error &&
        error.message &&
        (error.message.includes('interaction_required') ||
          error.message.includes('login_required') ||
          error.message.includes('no_account'))
      ) {
        // Set flag before logout to prevent concurrent logout attempts
        this.isLoggingOut = true;
        msalInstance.logoutRedirect();
      } else {
        // Log error for diagnostics, do not force logout on transient errors
        if (typeof logger !== 'undefined') {
          logger.error(error as Error, { origin: 'getTokenSilently' });
        }
      }
      return null;
    }
  }
}

export default MsalInstanceManager;
